import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/utils/cn';

import { IconButton } from './IconButton';

type Size = 'sm' | 'md' | 'lg' | 'xl';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalRootProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  closeOnScrim?: boolean;
  size?: Size;
  className?: string;
  ariaLabel?: string;
}

/**
 * Headless modal shell: portal + scrim + focus trap + ESC, with the ds-modal
 * chrome (white card, rounded corners, slide-in). Compose your own head/body/foot
 * inside, or use the higher-level <Modal> for the standard layout.
 */
export function ModalRoot({
  open,
  onClose,
  children,
  closeOnScrim = true,
  size = 'md',
  className,
  ariaLabel,
}: ModalRootProps) {
  const ref = useRef<HTMLDivElement>(null);
  const lastActiveRef = useRef<Element | null>(null);
  // onClose changes identity on every parent render. Read it through a ref so
  // the open-effect's deps stay stable — otherwise the effect re-runs on every
  // keystroke inside the modal and moves focus back to the first element
  // (typically the close button), making form inputs unusable.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    lastActiveRef.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      } else if (e.key === 'Tab') {
        const root = ref.current;
        if (!root) return;
        const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null,
        );
        if (items.length === 0) return;
        const first = items[0];
        const last = items[items.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);

    queueMicrotask(() => {
      const root = ref.current;
      if (!root) return;
      const first = root.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    });

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      if (lastActiveRef.current instanceof HTMLElement) {
        lastActiveRef.current.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  const sizeClass =
    size === 'sm' ? 'size-sm'
    : size === 'lg' ? 'size-lg'
    : size === 'xl' ? 'size-xl'
    : '';

  return createPortal(
    <div
      className="ds-scrim"
      onMouseDown={(e) => {
        if (closeOnScrim && e.target === e.currentTarget) onCloseRef.current();
      }}
    >
      <div
        ref={ref}
        className={cn('ds-modal', sizeClass, className)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  size?: Size;
  children: ReactNode;
  footer?: ReactNode;
  closeOnScrim?: boolean;
  className?: string;
  bodyClassName?: string;
  hideCloseButton?: boolean;
}

/**
 * Standard modal with title row, scrolling body, and optional footer.
 * For free-form layouts (existing dialogs that compose their own head/foot),
 * use <ModalRoot> directly.
 */
export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size,
  children,
  footer,
  closeOnScrim,
  className,
  bodyClassName,
  hideCloseButton,
}: ModalProps) {
  return (
    <ModalRoot
      open={open}
      onClose={onClose}
      size={size}
      closeOnScrim={closeOnScrim}
      className={className}
      ariaLabel={typeof title === 'string' ? title : undefined}
    >
      {(title || !hideCloseButton) && (
        <div className="ds-modal-head">
          {title && <h2>{title}</h2>}
          {subtitle && <span className="ds-modal-sub">{subtitle}</span>}
          {!hideCloseButton && (
            <IconButton
              icon="x"
              label="Close"
              className="ds-modal-close"
              onClick={onClose}
            />
          )}
        </div>
      )}
      <div className={cn('ds-modal-body', bodyClassName)}>{children}</div>
      {footer && <div className="ds-modal-foot">{footer}</div>}
    </ModalRoot>
  );
}
